import { List } from '@material-ui/core'
import { TFunction } from 'i18next'
import { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import {
  getApartmentApplications,
  updateApplicationStatus
} from '../../../actions/applications'
import { UpdateApplicationStatus } from '../../../actions/types'
import { NestedList } from '../../../components/Display'
import { selectIsAdminUser } from '../../../selectors/users'
import { Application } from '../../../types/applications'
import { StateInterface } from '../../../types/states'
import { formatToReadableDate } from '../../../utils/dates'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import UpdateApplicationStatusFrom from '../../Application/Forms/UpdateApplicationStatusForm'
import {
  applicationInformationOrder,
  getApplicationDataWithTranslations,
  applicationInformationOrderAdmin
} from '../values/handleApplicationsData'
import ApplicationsApi from '../../../services/applications'
import ApplicantsAdminList from './ApplicantsAdminList'

interface OwnProps {
  apartmentId?: string
  apartmentUserId?: string
  /** admin dialog: store data comes from GET /applications without applicants */
  refetchOnMount?: boolean
  applications: Application[]
  t: TFunction
}

interface StateProps {
  isAdmin: boolean
}
interface DispatchProps {
  updateApplicationStatus: UpdateApplicationStatus
  fetchApartmentApplications: (apartmentId: string) => void
}

type ApplicationsListProps = OwnProps & DispatchProps & StateProps

const ApplicationsList: FC<ApplicationsListProps> = ({
  apartmentId,
  apartmentUserId,
  refetchOnMount,
  t,
  applications,
  updateApplicationStatus,
  fetchApartmentApplications,
  isAdmin
}) => {
  const [landlordChatUsage, setLandlordChatUsage] = useState<Record<string, boolean>>({})

  // GET /applications strips `applicants`; the apartment endpoint returns them
  useEffect(() => {
    if (refetchOnMount && isAdmin && apartmentId) {
      fetchApartmentApplications(apartmentId)
    }
  }, [refetchOnMount, apartmentId, isAdmin, fetchApartmentApplications])

  useEffect(() => {
    function fetchChatUsage() {
      if (!isAdmin || !apartmentId || !apartmentUserId) return
      const usage: Record<string, boolean> = {}
      Promise.all(
        applications.map((application) => {
          return ApplicationsApi.checkIfLandlordUsedChat(
            apartmentId,
            application.applicationId,
            apartmentUserId
          )
            .then((response) => {
              usage[application.applicationId] = response.landlordUsedChat
            })
            .catch((error) => {
              usage[application.applicationId] = false
              console.error('Error checking if chat is used', error)
            })
        })
      ).then(() => {
        setLandlordChatUsage(usage)
      })
    }
    fetchChatUsage()
  }, [applications, apartmentId, apartmentUserId, isAdmin])

  return (
    <List component='nav'>
      {applications &&
        applications.map((application: Application) => {
          const { applicationId, status, createdAt } = application
          const landlordUsedChat = landlordChatUsage[applicationId] || false

          const readableCreatedAt = formatToReadableDate(
            createdAt,
            'HH:MM dd.mm.yyyy'
          )
          const data = getApplicationDataWithTranslations(application, isAdmin)
          const listItemText = `${data.name} - +${data.phone} - ${data.email
            } - ${t(`application.status.${status}`)} ${data.phoneShown ? ' - (P) ' : ''} 
             ${landlordUsedChat ? ' - (C) ' : ''} - ${readableCreatedAt}`
          return (
            <NestedList
              key={applicationId}
              t={t}
              translationPath='application.list.'
              id={applicationId}
              listItemText={listItemText}
              data={data}
              listName='applicationsList'
              informationOrder={
                isAdmin
                  ? applicationInformationOrderAdmin
                  : applicationInformationOrder
              }
              splitIntoTwoColumns
              Buttons={
                isAdmin && application.applicants ? (
                  <ApplicantsAdminList
                    t={t}
                    apartmentId={application.apartmentId || apartmentId}
                    applicationId={applicationId}
                    applicants={application.applicants}
                  />
                ) : null
              }
              Form={
                <UpdateApplicationStatusFrom
                  t={t}
                  title='apartment.adminInfo.button'
                  applicationId={applicationId}
                  updateApplicationStatus={updateApplicationStatus}
                  initialValues={{
                    status,
                    applicationId
                  }}
                />
              }
            />
          )
        })}
    </List>
  )
}

const mapStateToProps = (state: StateInterface) => ({
  isAdmin: selectIsAdminUser(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateApplicationStatus: bindSubmitActionToPromise(
    dispatch,
    updateApplicationStatus
  ),
  fetchApartmentApplications: (apartmentId: string) =>
    dispatch(getApartmentApplications({ apartmentId }))
})

export default connect<StateProps, DispatchProps, OwnProps, StateInterface>(
  mapStateToProps,
  mapDispatchToProps
)(ApplicationsList)
